import React, { useState, useCallback, useMemo } from 'react';
import '../App.css';

const API_URL = window.location.origin;

export function TelegramAuthCode({ internalId, onAuthSuccess, onBack }) {
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [step, setStep] = useState('code');
    const [userExists, setUserExists] = useState(null);
    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [unverifiedEmail, setUnverifiedEmail] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [checkingVerification, setCheckingVerification] = useState(false);

    // Состояния для смены email - вынесены отдельно
    const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
    const [newEmailForChange, setNewEmailForChange] = useState('');
    const [emailChangeLoading, setEmailChangeLoading] = useState(false);
    const [emailChangeError, setEmailChangeError] = useState(null);

    const handleCodeChange = (index, value) => {
        if (value.length > 1) return;
        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        if (value && index < 5) {
            const nextInput = document.getElementById(`code-${index + 1}`);
            if (nextInput) nextInput.focus();
        }
    };

    const checkEmailVerification = async () => {
        setCheckingVerification(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/api/auth/check-verification/${internalId}`);
            const data = await response.json();

            if (data.success && data.emailVerified) {
                setShowVerifyModal(false);

                const completeResponse = await fetch(`${API_URL}/api/auth/telegram/complete-verification`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ internalId: internalId })
                });

                const completeData = await completeResponse.json();

                if (completeData.success) {
                    localStorage.setItem('token', completeData.token);
                    localStorage.setItem('user', JSON.stringify(completeData.user));
                    onAuthSuccess(completeData.user);
                } else {
                    setError(completeData.error || 'Ошибка входа');
                }
            } else {
                setError('Email еще не подтвержден. Проверьте почту или нажмите "Отправить повторно".');
            }
        } catch (error) {
            console.error('Ошибка проверки:', error);
            setError('Ошибка соединения с сервером');
        } finally {
            setCheckingVerification(false);
        }
    };

    // Функция для повторной смены email
    const handleRetryEmailChange = async () => {
        if (!newEmailForChange.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            setEmailChangeError('Введите корректный email');
            return;
        }

        setEmailChangeLoading(true);
        setEmailChangeError(null);

        try {
            const response = await fetch(`${API_URL}/api/auth/retry-email-change`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: internalId,
                    newEmail: newEmailForChange
                })
            });

            const data = await response.json();

            if (data.success) {
                setUnverifiedEmail(newEmailForChange);
                setShowEmailChangeModal(false);
                setNewEmailForChange('');
                setError('✅ Email изменен! Новое письмо с подтверждением отправлено.');
                setTimeout(() => setError(null), 5000);
            } else {
                setEmailChangeError(data.error || 'Ошибка при смене email');
            }
        } catch (error) {
            console.error('Ошибка:', error);
            setEmailChangeError('Ошибка соединения с сервером');
        } finally {
            setEmailChangeLoading(false);
        }
    };

    const handleVerifyCode = async () => {
        const fullCode = code.join('');
        if (fullCode.length !== 6) {
            setError('Введите полный код из 6 цифр');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/api/auth/telegram/verify-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    internalId: parseInt(internalId),
                    code: fullCode
                })
            });

            const data = await response.json();

            if (data.success) {
                if (data.hasEmail && data.hasPassword && !data.emailVerified) {
                    if (data.token) {
                        localStorage.setItem('token', data.token);
                    }
                    setUnverifiedEmail(data.email || '');
                    setShowVerifyModal(true);
                    setLoading(false);
                    return;
                }

                if (data.hasEmail && data.hasPassword && data.emailVerified) {
                    localStorage.setItem('token', data.token);
                    localStorage.setItem('user', JSON.stringify(data.user));
                    onAuthSuccess(data.user);
                } else if (data.hasEmail) {
                    setUserExists(true);
                    setStep('register');
                } else {
                    setUserExists(false);
                    setStep('register');
                }
            } else {
                setError(data.error || 'Неверный код');
            }
        } catch (error) {
            console.error('Ошибка:', error);
            setError('Ошибка соединения с сервером');
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async () => {
        if (password !== confirmPassword) {
            setError('Пароли не совпадают');
            return;
        }

        if (password.length < 6) {
            setError('Пароль должен быть не менее 6 символов');
            return;
        }

        if (!userExists && !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            setError('Введите корректный email');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const endpoint = userExists ? '/api/auth/telegram/add-password' : '/api/auth/telegram/register';
            const body = userExists
                ? { internalId: parseInt(internalId), code: code.join(''), password }
                : { internalId: parseInt(internalId), code: code.join(''), email, password };

            const response = await fetch(`${API_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            const data = await response.json();

            if (data.success) {
                if (!data.user?.emailVerified) {
                    if (data.token) {
                        localStorage.setItem('token', data.token);
                    }
                    setUnverifiedEmail(email);
                    setShowVerifyModal(true);
                    setLoading(false);
                    return;
                }

                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
                onAuthSuccess(data.user);
            } else {
                setError(data.error || 'Ошибка при регистрации');
            }
        } catch (error) {
            console.error('Ошибка:', error);
            setError('Ошибка соединения с сервером');
        } finally {
            setLoading(false);
        }
    };

    const handleResendVerification = async () => {
        if (!unverifiedEmail || unverifiedEmail === '') {
            setError('Email не найден');
            return;
        }

        setResendLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: unverifiedEmail })
            });

            const data = await response.json();

            if (data.success) {
                setError('✅ Письмо отправлено! Проверьте почту');
                setTimeout(() => setError(null), 5000);
            } else {
                setError(data.error || 'Ошибка отправки');
            }
        } catch (error) {
            console.error('Ошибка:', error);
            setError('Ошибка соединения с сервером');
        } finally {
            setResendLoading(false);
        }
    };

    // ВАЖНО: Компоненты модальных окон создаются один раз и не пересоздаются
    const VerifyModalComponent = useMemo(() => {
        if (!showVerifyModal) return null;

        return (
            <div className="modal-overlay" onClick={() => setShowVerifyModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Подтверждение email</h2>
                        <button className="modal-close" onClick={() => setShowVerifyModal(false)}>×</button>
                    </div>
                    <div className="modal-body">
                        <div className="verify-icon">📧</div>
                        <p>Пожалуйста, подтвердите ваш email адрес:</p>
                        <p className="verify-email">{unverifiedEmail || 'Email не указан'}</p>
                        <p className="verify-text">
                            На указанный email было отправлено письмо с ссылкой для подтверждения.
                            Перейдите по ссылке, чтобы активировать аккаунт.
                        </p>

                        <button
                            className="confirm-verified-btn"
                            onClick={checkEmailVerification}
                            disabled={checkingVerification}
                        >
                            {checkingVerification ? 'Проверка...' : '✅ Я подтвердил email'}
                        </button>

                        <div className="verify-divider">
                            <span>или</span>
                        </div>

                        <div className="verify-actions">
                            <button
                                className="resend-verify-btn"
                                onClick={handleResendVerification}
                                disabled={resendLoading}
                            >
                                {resendLoading ? 'Отправка...' : '📧 Отправить письмо повторно'}
                            </button>
                            <button
                                className="change-email-modal-btn"
                                onClick={() => {
                                    setShowVerifyModal(false);
                                    setShowEmailChangeModal(true);
                                    setEmailChangeError(null);
                                    setNewEmailForChange('');
                                }}
                            >
                                ✏️ Ввели неверный email? Сменить
                            </button>
                            <button
                                className="close-verify-btn"
                                onClick={() => {
                                    setShowVerifyModal(false);
                                    onBack();
                                }}
                            >
                                ← Назад
                            </button>
                        </div>

                        {error && <div className="verify-error">{error}</div>}
                    </div>
                </div>
            </div>
        );
    }, [showVerifyModal, unverifiedEmail, checkingVerification, resendLoading, error]);

    const EmailChangeModalComponent = useMemo(() => {
        if (!showEmailChangeModal) return null;

        return (
            <div className="modal-overlay" onClick={() => setShowEmailChangeModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Сменить email</h2>
                        <button className="modal-close" onClick={() => setShowEmailChangeModal(false)}>×</button>
                    </div>
                    <div className="modal-body">
                        <div className="verify-icon">✉️</div>
                        <p>Текущий email:</p>
                        <p className="verify-email">{unverifiedEmail}</p>
                        <p>Введите новый email:</p>
                        <div className="code-input-group">
                            <input
                                type="email"
                                className="code-input-full"
                                placeholder="Новый email"
                                value={newEmailForChange}
                                onChange={(e) => setNewEmailForChange(e.target.value)}
                                autoFocus
                            />
                        </div>
                        <p className="verify-text-small">
                            После смены email на новый адрес будет отправлено письмо с подтверждением.
                            Сменить email можно раз в 3 часа.
                        </p>
                        {emailChangeError && <div className="verify-error">{emailChangeError}</div>}
                        <div className="verify-actions">
                            <button
                                className="confirm-verify-btn"
                                onClick={handleRetryEmailChange}
                                disabled={emailChangeLoading}
                            >
                                {emailChangeLoading ? 'Смена...' : 'Сменить email'}
                            </button>
                            <button
                                className="close-verify-btn"
                                onClick={() => setShowEmailChangeModal(false)}
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [showEmailChangeModal, unverifiedEmail, newEmailForChange, emailChangeLoading, emailChangeError]);

    const renderCodeStep = () => (
        <>
            <div className="auth-header">
                <h1>HydraVPN</h1>
                <p>Введите код из Telegram</p>
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                    Код отправлен в Telegram боту
                </p>
            </div>

            {error && <div className="auth-error">{error}</div>}

            <div className="code-inputs">
                {code.map((digit, index) => (
                    <input
                        key={index}
                        id={`code-${index}`}
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength="1"
                        className="code-input"
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        autoFocus={index === 0}
                    />
                ))}
            </div>

            <button className="auth-button" onClick={handleVerifyCode} disabled={loading}>
                {loading ? 'Проверка...' : 'Подтвердить'}
            </button>

            <div className="auth-footer">
                <button className="auth-switch" onClick={onBack}>
                    ← Назад
                </button>
            </div>
        </>
    );

    const renderRegisterStep = () => (
        <>
            <div className="auth-header">
                <h1>HydraVPN</h1>
                <p>{userExists ? 'Создайте пароль для аккаунта' : 'Завершите регистрацию'}</p>
            </div>

            {error && <div className="auth-error">{error}</div>}

            <form onSubmit={(e) => { e.preventDefault(); handleRegister(); }} className="auth-form">
                {!userExists && (
                    <div className="form-group">
                        <input
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>
                )}

                <div className="form-group">
                    <input
                        type="password"
                        placeholder="Пароль"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                    />
                </div>

                <div className="form-group">
                    <input
                        type="password"
                        placeholder="Подтвердите пароль"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={6}
                    />
                </div>

                <button type="submit" className="auth-button" disabled={loading}>
                    {loading ? 'Сохранение...' : (userExists ? 'Сохранить пароль' : 'Зарегистрироваться')}
                </button>
            </form>

            <div className="auth-footer">
                <button className="auth-switch" onClick={() => setStep('code')}>
                    ← Назад
                </button>
            </div>
        </>
    );

    return (
        <div className="auth-page">
            <div className="auth-container">
                <div className="auth-card">
                    {step === 'code' && renderCodeStep()}
                    {step === 'register' && renderRegisterStep()}
                </div>
            </div>
            {VerifyModalComponent}
            {EmailChangeModalComponent}
        </div>
    );
}